import { ComponentType, createContext, FC, ReactNode, useContext } from 'react';

import { wrapDisplayName } from '@mopla/utils';

import { ScheduleValue, useSchedule } from '../hooks/useSchedule';

// Copy the existing ScheduleValue type
type ScheduleContextValue = ScheduleValue;

// Create context with a meaningful default value (null for mandatory pattern)
const ScheduleContext = createContext<ScheduleContextValue | null>(null);

// Provider component
export const ScheduleProvider: FC<{
	children: ReactNode;
}> = ({ children }) => {
	// Use the original hook only once inside the provider
	const scheduleValue = useSchedule();

	return (
		<ScheduleContext.Provider value={scheduleValue}>
			{children}
		</ScheduleContext.Provider>
	);
};

// Consumer hook
export const useScheduleContext = (): ScheduleContextValue => {
	const context = useContext(ScheduleContext);

	if (context === null) {
		throw new Error(
			'useScheduleContext must be used within a ScheduleProvider'
		);
	}

	return context;
};

export const withScheduleContext = <P extends object>(
	WrappedComponent: ComponentType<P>
) => {
	const Wrapper: FC<P> = (props) => {
		return (
			<ScheduleProvider>
				<WrappedComponent {...props} />
			</ScheduleProvider>
		);
	};

	Wrapper.displayName = wrapDisplayName(
		WrappedComponent,
		'withScheduleContext'
	);

	return Wrapper;
};
